import React from 'react';
import { Zap, Globe, Clock, TrendingUp } from 'lucide-react';
import { formatLcp } from '../formatLcp';
import MetricHelp from './MetricHelp';
import {
  METRIC_HELP,
  clsStatus,
  clsTextClass,
  durationStatus,
  durationTextClass,
  formatCls,
  formatOptionalDuration,
  scoreStatus,
  scoreTextClass,
} from '../metrics';

const VITALS = [
  {
    key: 'lcp',
    label: 'LCP',
    format: (record) => formatLcp(record.lcp),
    tone: (record) => durationTextClass(record.lcp, 2500, 4000),
    status: (record) => durationStatus(record.lcp, 2500, 4000),
  },
  {
    key: 'inp',
    label: 'INP',
    format: (record) => formatOptionalDuration(record.inp),
    tone: (record) => durationTextClass(record.inp, 200, 500),
    status: (record) => durationStatus(record.inp, 200, 500),
  },
  {
    key: 'cls',
    label: 'CLS',
    format: (record) => formatCls(record.cls),
    tone: (record) => clsTextClass(record.cls),
    status: (record) => clsStatus(record.cls),
  },
  {
    key: 'ti',
    label: 'TI',
    format: (record) => formatOptionalDuration(record.ti),
    tone: (record) => durationTextClass(record.ti, 2500, 5000),
    status: (record) => durationStatus(record.ti, 2500, 5000),
  },
];

const Header = ({ latestRecords, loading }) => {

  // Group records by tool and filter out tools without names
  const groupedByTool = React.useMemo(() => {
    if (!latestRecords || latestRecords.length === 0) return [];
    
    const grouped = {};
    
    latestRecords.forEach(record => {
      // Skip records without tool names
      if (!record.tool || record.tool.trim() === '') return;
      
      if (!grouped[record.tool]) {
        grouped[record.tool] = [];
      }
      grouped[record.tool].push(record);
    });
    
    // Convert to array and sort by tool name
    return Object.entries(grouped)
      .map(([tool, records]) => ({ tool, records }))
      .sort((a, b) => a.tool.localeCompare(b.tool));
  }, [latestRecords]);

  if (loading) {
    return (
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gray-200 rounded-lg animate-pulse"></div>
                <div className="h-6 bg-gray-200 rounded w-48 animate-pulse"></div>
              </div>
              <div className="flex space-x-4">
                {[1, 2].map((i) => (
                  <div key={i} className="w-64 h-32 bg-gray-200 rounded-lg animate-pulse"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Display-Sales Speedcheck</h1>
              <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                Live Monitoring
              </span>
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <Clock className="w-4 h-4" />
              <span>Last updated: {new Date().toLocaleTimeString()}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6">
            {groupedByTool.map(({ tool, records }) => (
              <div key={tool} className="metric-card">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <Globe className="w-5 h-5 text-blue-600" />
                    <span className="text-lg font-semibold text-gray-900">{tool}</span>
                  </div>
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                    {records.length} domain{records.length !== 1 ? 's' : ''}
                  </span>
                </div>
                
                <div className="space-y-4">
                  {records.map((record) => (
                    <div key={record.id} className="border-l-4 border-blue-200 pl-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">{record.domain}</span>
                        <span className="text-xs text-gray-500">
                          {record.datum} at {record.time}
                        </span>
                      </div>
                      
                      <div>
                        <div className="flex items-center justify-between">
                          <span className="metric-label">Page Score</span>
                          <span className={`metric-value ${scoreTextClass(record.onPageScore)}`}>
                            {record.onPageScore.toFixed(1)}
                          </span>
                        </div>
                        <div className="flex items-center space-x-1 mt-1">
                          <TrendingUp className="w-3 h-3 text-gray-400" />
                          <span className="text-xs text-gray-500">{scoreStatus(record.onPageScore)}</span>
                        </div>
                      </div>

                      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                        {VITALS.map((vital) => {
                          const status = vital.status(record);

                          return (
                            <div key={vital.key}>
                              <div className="flex items-center gap-1">
                                <span className="metric-label">{vital.label}</span>
                                <MetricHelp label={vital.label} text={METRIC_HELP[vital.key]} />
                              </div>
                              <div className={`mt-1 text-xl font-bold ${vital.tone(record)}`}>
                                {vital.format(record)}
                              </div>
                              {status && (
                                <div className="mt-1 flex items-center space-x-1">
                                  <Clock className="w-3 h-3 text-gray-400" />
                                  <span className="text-xs text-gray-500">{status}</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          {groupedByTool.length === 0 && (
            <div className="text-center py-8">
              <Globe className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No data available</h3>
              <p className="text-gray-500">No speed test data found with valid tool names.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Header;
